import { useCallback, useState } from "react";
import {
    AvailableProductsAndCommissionsResponse,
    AvailableUserTypesResponse,
} from "../../createDownline.models";
import { GetAvailableProductsAndCommissions, GetAvailableUserTypes } from "../../createDownline.services";
import { getApiErrorMessage } from "../../../../../../utils/getApiErrorMessage";
import { shouldShowErrorToast } from "../../../../../../utils/apiRequestError";
import { toast } from "react-toastify";

interface UseContentProps {
    selectedUserId: number | null;
}

export const useContent = ({ selectedUserId }: UseContentProps) => {
    const [agencyTypes, setAgencyTypes] = useState<AvailableUserTypesResponse[]>([]);
    const [agencyTypeLoading, setAgencyTypeLoading] = useState(false);
    const [productsAndCommissions, setProductsAndCommissions] = useState<
        AvailableProductsAndCommissionsResponse[]
    >([]);
    const [productLoading, setProductLoading] = useState(false);

    const fetchAgencyTypes = useCallback(async () => {
        if (!selectedUserId) return { error: "Missing user id" };

        setAgencyTypeLoading(true);
        try {
            const response = await GetAvailableUserTypes(selectedUserId);

            if (!response.flagStatus) {
                const errorMessage = response.error ?? "Failed to fetch agency types";
                toast.error(errorMessage);
                return { error: errorMessage };
            }

            setAgencyTypes(response.value);
            return { data: response.value };
        } catch (error) {
            const message = getApiErrorMessage(error, "Failed to fetch agency types");
            if (shouldShowErrorToast(error)) {
                toast.error(message);
            }
            return { error: message };
        } finally {
            setAgencyTypeLoading(false);
        }
    }, [selectedUserId]);

    const fetchProductsAndCommissions = useCallback(async () => {
        if (!selectedUserId) return { error: "Missing user id" };

        setProductLoading(true);
        try {
            const response =  await GetAvailableProductsAndCommissions(selectedUserId);
            
            if (!response.flagStatus) {
                const errorMessage = response.error ?? "Failed to fetch products";
                toast.error(errorMessage);
                return { error: errorMessage };
            }

            setProductsAndCommissions(response.value);
            return { data: response.value };
        } catch (error) {
            const message = getApiErrorMessage(error, "Failed to fetch products");
            if (shouldShowErrorToast(error)) {
                toast.error(message);
            }
            return { error: message };
        } finally {
            setProductLoading(false);
        }
    }, [selectedUserId]);

    const reset = useCallback(() => {
        setAgencyTypes([]);
        setProductsAndCommissions([]);
    }, []);

    return {
        agencyTypes,
        agencyTypeLoading,
        productsAndCommissions,
        productLoading,
        fetchAgencyTypes,
        fetchProductsAndCommissions,
        reset,
    };
};
